import React, { createContext, useContext, useState, useEffect } from "react";
import { getShippingDetails, ShippingDetail } from "@/services/shippingApiService";
import { useSelectedStudent } from "./SelectedStudentContext";
import { useOverviewContext } from "./OverviewContext";
import { useOktaAuth } from "@okta/okta-react";
import { shouldSkipStudentScopedBackendApi } from "@/utils/toolSettingsUtils";

interface ShippingContextType {
  shippingData: ShippingDetail[];
  loading: boolean;
  error: string | null;
  refreshShipping: () => Promise<void>;
}
const ShippingContext = createContext<ShippingContextType>({
  shippingData: [],
  loading: false,
  error: null,
  refreshShipping: async () => {},     
});

export const ShippingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedStudent } = useSelectedStudent();
  const { userRoleVal } = useOverviewContext();
  const { authState } = useOktaAuth();

  const [shippingData, setShippingData] = useState<ShippingDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accessToken = authState?.accessToken?.accessToken;

  const fetchShipping = async () => {
    if (!selectedStudent?.identityId || !accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getShippingDetails(selectedStudent.identityId, accessToken);
      setShippingData(data ?? []);
    } catch (err: any) {
      setError(err.message || "Failed to load shipping details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      !authState?.isAuthenticated ||
      !selectedStudent?.identityId ||
      !accessToken ||
      shouldSkipStudentScopedBackendApi(selectedStudent.identityId, userRoleVal)
    ) {
      return;
    }
    fetchShipping();
  }, [authState?.isAuthenticated, selectedStudent?.identityId, accessToken, userRoleVal]);

  return (
    <ShippingContext.Provider value={{
        shippingData,
        loading,
        error,
        refreshShipping: fetchShipping,
      }}
    >
      {children}
    </ShippingContext.Provider>
  );
};
export const useShipping = () => useContext(ShippingContext);
